import { useCallback, useEffect, useState } from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  StopHero,
  StopTile,
  cycleStop,
  KIND_ORDER,
  todayIso,
  stopIsMine,
  phoneKey,
} from "@/pages/mobile/MobileStop";

export default function MobileMyDay() {
  const { user } = useAuth();
  const [stops, setStops] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [mineOnly, setMineOnly] = useState(true);
  const [unreadKeys, setUnreadKeys] = useState(new Set());
  const date = todayIso();
  const me = user?.name || user?.email;

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs/schedule", {
      params: { month: date.slice(0, 7) },
    });
    setStops(
      (data.events || [])
        .filter((e) => e.date === date)
        .sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9)),
    );
    api
      .get("/tasks", { params: { date, carry: true } })
      .then((r) => setTasks(r.data))
      .catch(() => {});
  }, [date]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);
  useEffect(() => {
    api
      .get("/sms/unread")
      .then(({ data }) =>
        setUnreadKeys(
          new Set((data.items || []).map((m) => phoneKey(m.from)).filter(Boolean)),
        ),
      )
      .catch(() => {});
  }, []);

  const KIND_CHIPS = [
    ["all", "All"],
    ["Template", "Templ."],
    ["Install", "Install"],
    ["Service", "Service"],
  ];
  const [kindFilter, setKindFilter] = useState(
    () => localStorage.getItem("mobile_kind_filter") || "all",
  );
  useEffect(() => {
    localStorage.setItem("mobile_kind_filter", kindFilter);
  }, [kindFilter]);

  const base = mineOnly ? stops.filter((s) => stopIsMine(s, user)) : stops;
  const visible = base.filter(
    (s) => kindFilter === "all" || (s.entity === "service" ? "Service" : s.kind) === kindFilter,
  );
  const done = visible.filter((s) => s.stop_status?.status === "Done").length;
  const open = visible.filter((s) => s.stop_status?.status !== "Done");
  const finished = visible.filter((s) => s.stop_status?.status === "Done");
  const heroStop = open.find((s) => s.stop_status?.status === "On site") || open[0];
  const others = visible.filter((s) => s !== heroStop && s.stop_status?.status !== "Done");
  const countFor = (k) =>
    base.filter((s) => k === "all" || (s.entity === "service" ? "Service" : s.kind) === k).length;
  const [showFinished, setShowFinished] = useState(
    () => localStorage.getItem("mobile_show_done") === "1",
  );
  const idxOf = (s) => visible.indexOf(s) + 1;

  const myTasks = tasks.filter((t) => (mineOnly ? t.assignee === me || !t.assignee : true));

  const apply = (stop, next) =>
    setStops((list) =>
      list.map((e) =>
        e.job_id === stop.job_id &&
        e.kind === stop.kind &&
        (e.stop_id || "") === (stop.stop_id || "")
          ? { ...e, stop_status: { ...e.stop_status, status: next } }
          : e,
      ),
    );
  const toggleTask = async (t) => {
    await api.patch(`/tasks/${t.id}/done`, { done: !t.done });
    setTasks((list) => list.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)));
  };

  const d = new Date(`${date}T00:00:00`);
  const dow = d.toLocaleDateString("en-US", { weekday: "long" });
  const md = d.toLocaleDateString("en-US", { month: "long", day: "numeric" });

  return (
    <div className="px-4 pt-5" data-testid="mobile-myday">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-title m-display text-3xl text-[#c39e6d]" data-testid="mobile-myday-title">
            Today
          </h1>
          <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            {dow}, {md}
          </p>
          {visible.length > 0 && (
            <span
              data-testid="mobile-myday-completed-badge"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-300"
            >
              <CheckCircle size={13} weight="fill" />
              {done} completed today
            </span>
          )}
        </div>
        <div className="shrink-0 text-right" data-testid="mobile-myday-summary">
          <div className="font-mono-data text-3xl font-bold leading-none text-zinc-100">
            {done}
            <span className="text-zinc-600">/{visible.length}</span>
          </div>
          <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Done
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2.5" data-testid="mobile-myday-priority">
        <PriorityCard tone="lime" label="Completed" value={done} />
        <PriorityCard
          tone="lilac"
          label="On site"
          value={visible.filter((s) => s.stop_status?.status === "On site").length}
        />
        <PriorityCard tone="cyan" label="Open" value={open.length} />
      </div>

      <div className="mt-4 flex gap-2">
        {[
          [true, "My stops"],
          [false, "Whole shop"],
        ].map(([v, l]) => (
          <button
            key={l}
            data-testid={`mobile-myday-filter-${v ? "mine" : "shop"}`}
            onClick={() => setMineOnly(v)}
            className={`h-9 flex-1 rounded-xl border text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              mineOnly === v
                ? "border-[#c39e6d] bg-[#c39e6d] text-slate-950"
                : "border-zinc-800 bg-[#0F172A] text-zinc-400 hover:bg-zinc-800"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-4 gap-1.5" data-testid="mobile-myday-kinds">
        {KIND_CHIPS.map(([k, l]) => (
          <button
            key={k}
            type="button"
            data-testid={`mobile-myday-kind-${k.toLowerCase()}`}
            onClick={() => setKindFilter(k)}
            className={`h-8 min-w-0 truncate rounded-full border px-2 text-[10px] font-bold uppercase tracking-wider transition-colors duration-150 ${
              kindFilter === k
                ? "border-[#c39e6d]/60 bg-[#c39e6d]/15 text-[#c39e6d]"
                : "border-zinc-800 text-zinc-400 hover:bg-[#0F172A]"
            }`}
          >
            {l}{" "}
            <span className="ml-0.5 font-mono-data opacity-70">{countFor(k)}</span>
          </button>
        ))}
      </div>

      {myTasks.length > 0 && (
        <div
          className="mt-5 rounded-2xl border border-zinc-800 bg-[#0F172A]/60 p-4"
          data-testid="mobile-myday-tasks"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Tasks · {myTasks.filter((t) => t.done).length} of {myTasks.length} done
          </div>
          <div className="mt-3 space-y-2">
            {myTasks.map((t) => (
              <button
                key={t.id}
                data-testid={`mobile-task-${t.id}`}
                onClick={() => toggleTask(t)}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-150 ${
                  t.done
                    ? "border-emerald-500/30 bg-[#080C14]"
                    : "border-zinc-800 bg-[#080C14] hover:border-zinc-700"
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border ${
                    t.done
                      ? "border-emerald-500 bg-emerald-500 text-black"
                      : "border-zinc-600"
                  }`}
                >
                  {t.done && <CheckCircle size={14} weight="bold" />}
                </span>
                <span
                  className={`min-w-0 flex-1 truncate text-sm font-semibold ${
                    t.done ? "text-zinc-500 line-through" : "text-zinc-200"
                  }`}
                >
                  {t.title}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {visible.length === 0 && (
        <div
          data-testid="mobile-myday-empty"
          className="mt-6 rounded-2xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500"
        >
          {mineOnly ? "Nothing assigned to you today." : "Nothing scheduled today."}
        </div>
      )}

      {heroStop && (
        <div className="mt-5" data-testid="mobile-myday-next">
          <div className="mb-2.5 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#c39e6d]" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#c39e6d]">
              {heroStop.stop_status?.status === "On site" ? "In progress" : "Up next"}
            </span>
          </div>
          <StopHero
            s={heroStop}
            onCycle={() => cycleStop(heroStop, (next) => apply(heroStop, next))}
            onReschedule={load}
          />
        </div>
      )}

      {others.length > 0 && (
        <div className="mt-7" data-testid="mobile-myday-others">
          <div className="mb-3 flex items-center gap-3">
            <span className="h-px flex-1 bg-zinc-800" />
            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500">
              Other stops
            </span>
            <span className="h-px flex-1 bg-zinc-800" />
          </div>
          <div className="space-y-2.5">
            {others.map((s) => (
              <StopTile
                key={`${s.job_id}-${s.kind}-${s.stop_id || ""}`}
                s={s}
                index={idxOf(s)}
              />
            ))}
          </div>
        </div>
      )}

      {finished.length > 0 && (
        <div className="mt-6" data-testid="mobile-myday-finished">
          <button
            type="button"
            data-testid="mobile-myday-finished-toggle"
            onClick={() => setShowFinished((v) => !v)}
            className="flex w-full items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-400"
          >
            <CheckCircle size={13} weight="bold" /> Done today · {finished.length}
            <span className="h-px flex-1 bg-zinc-800" />
            <span className="text-zinc-500">{showFinished ? "hide" : "show"}</span>
          </button>
          {showFinished && (
            <div className="mt-3 space-y-2.5 opacity-70">
              {finished.map((s) => (
                <StopTile
                  key={`${s.job_id}-${s.kind}-${s.stop_id || ""}`}
                  s={s}
                  index={idxOf(s)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const PRIORITY_TONES = {
  lime: "border-[#c39e6d]/40 bg-[#c39e6d]/10 text-[#c39e6d]",
  lilac: "border-[#C084FC]/40 bg-[#C084FC]/10 text-[#C084FC]",
  cyan: "border-[#00F2FE]/40 bg-[#00F2FE]/10 text-[#00F2FE]",
};
function PriorityCard({ tone, label, value }) {
  return (
    <div
      data-testid={`mobile-priority-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-2xl border p-3 ${PRIORITY_TONES[tone]}`}
    >
      <div className="font-mono-data text-2xl font-black leading-none">{value}</div>
      <div className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.14em] opacity-80">
        {label}
      </div>
    </div>
  );
}
